import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, Grid, Box, Button, Pagination, Tabs, Tab, Chip, useMediaQuery, Theme } from '@mui/material';
import { getGamesByStatus, getGamesByStatusForUser, getPublicGameStatuses, getProfileGameStatuses, getPlatforms, PlatformFilterType } from '../../services/api';
import GameCard from '../GameCard';
import { UserProfile } from '../../types/UserProfile';
import { Game, Platform } from '../../types/Game';
import LoadingPlaceholder from '../LoadingPlaceholder';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import GameListFilter from '../filter/GameListFilter';

const GAMES_PER_PAGE = 30;

interface GameLibraryTabProps {
  profile: UserProfile | null;
  isPublic: boolean;
  gameCounts: number[];
  mainTabValue: string;
}

const GameLibraryTab: React.FC<GameLibraryTabProps> = ({
  profile,
  isPublic,
  gameCounts,
  mainTabValue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initialStatus = parseInt(queryParams.get('status') || '1', 10);
  const initialPage = parseInt(queryParams.get('page') || '1', 10);
  const initialSearchQuery = queryParams.get('query') || '';
  const initialOrder = queryParams.get('order') || 'default';
  const initialPlatforms = queryParams.get('platforms') ? queryParams.get('platforms')!.split(',').map(Number) : [];
  const initialFilterType = (queryParams.get('filter_type') as PlatformFilterType) || 'user_platform';

  const [tabValue, setTabValue] = useState(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);
  const [priorityMode, setPriorityMode] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>(initialPlatforms);
  const [platformFilterType, setPlatformFilterType] = useState<PlatformFilterType>(initialFilterType);

  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // プラットフォーム一覧を取得
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const data = await getPlatforms();
        setPlatforms(data);
      } catch (error) {
        console.error('プラットフォームの取得に失敗しました:', error);
      }
    };
    fetchPlatforms();
  }, []);

  // フィルターがアクティブかどうかを判定
  const hasActiveFilter = useMemo(() => {
    return selectedPlatforms.length > 0 || platformFilterType !== 'user_platform';
  }, [selectedPlatforms, platformFilterType]);

  // React Queryでゲーム一覧を取得
  const stableUsername = isPublic ? username : 'me';
  const {
    data: gamesData,
    isLoading,
  } = useQuery({
    queryKey: ['profileGames', stableUsername, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType],
    queryFn: async () => {
      if (isPublic) {
        return getGamesByStatusForUser(username!, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType);
      } else {
        return getGamesByStatus(tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType);
      }
    },
    enabled: mainTabValue === 'library',
    staleTime: isPublic
      ? 30000
      : (hasActiveFilter ? 4000 : 2000),
  });

  const games: Game[] = gamesData?.data.results || [];
  const totalGames = gamesData?.data.count || 0;
  const gamesTotalPages = Math.ceil(totalGames / GAMES_PER_PAGE);
  const loadingGames = isLoading && !gamesData;

  // ステータス取得用のgameIds
  const gameIds = useMemo(() => games.map(game => game.game_id), [games]);

  // React Queryでゲームステータスを取得
  const { data: gameStatusesData } = useQuery({
    queryKey: ['profileGameStatuses', stableUsername, gameIds],
    queryFn: async () => {
      if (isPublic) {
        return getPublicGameStatuses(username!, gameIds);
      } else {
        return getProfileGameStatuses(gameIds);
      }
    },
    enabled: gameIds.length > 0,
    staleTime: isPublic
      ? 30000
      : (hasActiveFilter ? 4000 : 2000),
  });

  const gameStatuses: {
    [key: number]: {
      status: number | null;
      score: number | null;
      review_text: string | null;
      play_start_date: string | null;
      play_end_date: string | null;
      play_time: number | null;
      sub_status: number[] | null;
      score_tags: string[] | null;
      platform: number[] | null;
      priority?: number;
    }
  } = gameStatusesData || {};

  // URLを更新する関数
  const updateUrl = useCallback((status: number, pg: number, query: string, ord: string, platformIds: number[] = [], filterType: PlatformFilterType = 'user_platform') => {
    const media = queryParams.get('media') || 'game';
    const params = new URLSearchParams({
      status: status.toString(),
      page: pg.toString(),
      query,
      order: ord,
      tab: mainTabValue,
      media,
    });
    if (platformIds.length > 0) {
      params.set('platforms', platformIds.join(','));
    }
    if (filterType !== 'user_platform') {
      params.set('filter_type', filterType);
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [isPublic, username, mainTabValue, navigate, queryParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentScroll = scrollPositionRef.current;
    setTabValue(newValue);
    setPage(1);
    updateUrl(newValue, 1, searchQuery, order, selectedPlatforms, platformFilterType);
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrl(tabValue, value, searchQuery, order, selectedPlatforms, platformFilterType);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
    updateUrl(tabValue, 1, query, order, selectedPlatforms, platformFilterType);
  };

  const handleOrderChange = (newOrder: string) => {
    setOrder(newOrder);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, newOrder, selectedPlatforms, platformFilterType);
  };

  const handlePlatformChange = (platformIds: number[]) => {
    setSelectedPlatforms(platformIds);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, platformIds, platformFilterType);
  };

  const handlePlatformFilterTypeChange = (filterType: PlatformFilterType) => {
    setPlatformFilterType(filterType);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, selectedPlatforms, filterType);
  };

  const handleFilterApply = (platformIds: number[], filterType: PlatformFilterType) => {
    setSelectedPlatforms(platformIds);
    setPlatformFilterType(filterType);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, platformIds, filterType);
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setOrder('default');
    setSelectedPlatforms([]);
    setPlatformFilterType('user_platform');
    setPage(1);
    updateUrl(tabValue, 1, '', 'default', [], 'user_platform');
  };

  // URLパラメータ変更の監視
  useEffect(() => {
    const newStatus = parseInt(queryParams.get('status') || '1', 10);
    if (newStatus !== tabValue) {
      setTabValue(newStatus);
      setPage(1);
    }
  }, [location.search]);

  const TabLabel: React.FC<{ label: string; count: number; isSelected: boolean }> = ({ label, count, isSelected }) => (
    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: 'center' }}>
      <Typography sx={{ mr: isSmallScreen ? 0 : 1, fontSize: isSmallScreen ? '0.75rem' : '0.8rem' }}>{label}</Typography>
      {isSelected ? (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'black',
            fontSize: isSmallScreen ? '0.625rem' : '0.75rem',
            height: isSmallScreen ? '16px' : '18px',
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.625rem' : '0.75rem' }}>
          /{count}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            maxWidth: '100%',
            '& .MuiTab-root': {
              minWidth: isSmallScreen ? 'auto' : 'inherit',
              padding: isSmallScreen ? '6px 8px' : '6px 16px',
              minHeight: '40px',
              borderRadius: '8px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }
          }}
        >
          <Tab label={<TabLabel label="プレイ済" count={gameCounts[0]} isSelected={tabValue === 1} />} value={1} />
          <Tab label={<TabLabel label="プレイ中" count={gameCounts[1]} isSelected={tabValue === 2} />} value={2} />
          <Tab label={<TabLabel label="中断" count={gameCounts[2]} isSelected={tabValue === 3} />} value={3} />
          <Tab label={<TabLabel label="積みゲー" count={gameCounts[3]} isSelected={tabValue === 4} />} value={4} />
          <Tab label={<TabLabel label={isSmallScreen ? "ウィッシュ" : "ウィッシュリスト"} count={gameCounts[4]} isSelected={tabValue === 5} />} value={5} />
        </Tabs>
      </Box>

      <Box sx={{ mt: 1 }}>
        <GameListFilter
          onSearch={handleSearch}
          onOrderChange={handleOrderChange}
          onClearFilter={handleClearFilter}
          totalGames={totalGames}
          initialSearchQuery={searchQuery}
          initialOrder={order}
          onPriorityModeChange={!isPublic ? setPriorityMode : undefined}
          priorityMode={priorityMode}
          currentStatus={tabValue}
          platforms={platforms}
          selectedPlatforms={selectedPlatforms}
          onPlatformChange={handlePlatformChange}
          platformFilterType={platformFilterType}
          onPlatformFilterTypeChange={handlePlatformFilterTypeChange}
          onFilterApply={handleFilterApply}
        />
      </Box>
      {loadingGames ? (
        <LoadingPlaceholder type="card" count={GAMES_PER_PAGE} />
      ) : (
        <>
          {games.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mt: 2
            }}>
              <Typography variant="h6" gutterBottom>
                まだゲームが登録されていません
              </Typography>
              {!isPublic && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    ゲームを検索して、ライブラリに追加しましょう
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/search')}
                  >
                    ゲームを探す
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {games.map((game) => (
                <Grid item xs={4} sm={3} md={2} key={game.game_id}>
                  <GameCard
                    game={game}
                    mode={isPublic ? 'public' : 'profile'}
                    username={isPublic ? username : undefined}
                    initialStatus={gameStatuses[game.game_id]?.status}
                    initialScore={gameStatuses[game.game_id]?.score}
                    initialReviewText={gameStatuses[game.game_id]?.review_text}
                    initialPlayStartDate={gameStatuses[game.game_id]?.play_start_date}
                    initialPlayEndDate={gameStatuses[game.game_id]?.play_end_date}
                    initialPlayTime={gameStatuses[game.game_id]?.play_time}
                    initialSubStatus={gameStatuses[game.game_id]?.sub_status}
                    initialScoreTags={gameStatuses[game.game_id]?.score_tags}
                    initialPlatform={gameStatuses[game.game_id]?.platform}
                    initialPriority={gameStatuses[game.game_id]?.priority}
                    priorityMode={priorityMode}
                    userSteamId={!isPublic ? profile?.steam_id : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {gamesTotalPages > 1 && (
            <Pagination
              count={gamesTotalPages}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}
    </>
  );
};

export default GameLibraryTab;
